import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../hook/useFetch";
import { IAddress } from "@common/Eircode/interface/IAddressForm";
import { UseFormMethods, useWatch } from "react-hook-form";
import { getEircodeFinderRoute } from "@api-routes/addressFinder";

export const useEirCodeFinder = <TFieldValues>(
    addressForm: UseFormMethods<TFieldValues> & Partial<UseFormMethods>,
    prefix?: string
) => {
    const { t } = useTranslation(["address", "common"]);

    const namePrefix = prefix ? `${prefix}.` : "";

    const [eircodeSelectedAddress, setEircodeSelectedAddress] = useState<number>();
    const [eircodeAddressList, setEircodeAddressList] = useState<IAddress[]>();

    const [getEircodeAddressListState, { get }] = useFetch();
    const eirCodeValue = useWatch<string>({
        name: `${namePrefix}code`,
        control: addressForm.control,
        defaultValue: "",
    });

    const submitEircode = async () => {
        setEircodeAddressList(undefined);

        if (eirCodeValue && (await addressForm.trigger(`${namePrefix}code`))) {
            const eirCodeFormatted = eirCodeValue.toUpperCase();
            get<{ addresses: IAddress[] }>(getEircodeFinderRoute(eirCodeFormatted)).then((result) => {
                if (result?.addresses) {
                    const { addresses } = result;
                    setEircodeAddressList(addresses);

                    if (addresses.length >= 1) {
                        fillAddress(addresses[0]);
                        setEircodeSelectedAddress(0);
                    }
                }
            });
        }
    };

    const selectAddress = (addressIndex: number) => {
        const addressSelected = eircodeAddressList ? eircodeAddressList[addressIndex] : undefined;
        setEircodeSelectedAddress(addressIndex);
        if (addressSelected) {
            fillAddress(addressSelected);
        }
    };

    const fillAddress = (newAddress: IAddress) => {
        addressForm.setValue(`${namePrefix}addressLine1`, newAddress.addressLine1);
        addressForm.setValue(`${namePrefix}addressLine2`, newAddress.addressLine2 ?? "");
        addressForm.setValue(`${namePrefix}addressLine3`, newAddress.addressLine3 ?? "");
        addressForm.setValue(`${namePrefix}town`, newAddress.town);
        addressForm.setValue(`${namePrefix}county`, newAddress.county);
        addressForm.setValue(`${namePrefix}code`, newAddress.eircode ?? "");
    };

    const onChangeEircode = (value: string) => {
        addressForm.setValue(`${namePrefix}addressLine1`, "");
        addressForm.setValue(`${namePrefix}addressLine2`, "");
        addressForm.setValue(`${namePrefix}addressLine3`, "");
        addressForm.setValue(`${namePrefix}town`, "");
        addressForm.setValue(`${namePrefix}county`, "");

        setEircodeAddressList(undefined);
        setEircodeSelectedAddress(undefined);
        addressForm.setValue(`${namePrefix}code`, value.replace(/\s/g, ""));
    };

    let errorMessage = "";

    if (getEircodeAddressListState.error) {
        errorMessage = t("common:genericError");
    } else if (eircodeAddressList && eircodeAddressList.length === 0) {
        errorMessage = t("address:noAddressFound");
    }

    return {
        eircodeAddressList,
        eircodeSelectedAddress,
        errorMessage,
        getEircodeAddressListState,
        onChangeEircode,
        selectAddress,
        submitEircode,
    };
};
